import React, { useState, useEffect, useRef, useCallback } from 'react';
import { tidyText } from './utils/tidyText';
import './SongBotz.css';

// Use relative URLs in dev so CRA proxy (package.json "proxy") forwards to backend; set REACT_APP_API_URL in production if different origin
const SONG_BOT_API = process.env.REACT_APP_API_URL || 'https://api.cinebotz.com';
const FRAME_KEYS = ['frame1', 'frame2', 'frame3', 'frame4'];

function normalize(str) {
  return (str || '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ');
}

function isCloseMatch(userGuess, correctAnswer) {
  const a = normalize(userGuess);
  const b = normalize(correctAnswer);
  if (!a) return false;
  if (a === b) return true;
  if (b.includes(a)) return true;
  if (a.includes(b)) return true;
  const aWords = a.split(/\s+/).filter(Boolean);
  const bWords = b.split(/\s+/).filter(Boolean);
  const matchCount = aWords.filter((w) => b.includes(w)).length;
  return matchCount >= Math.min(aWords.length, bWords.length) * 0.6;
}

export function SongBotz() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentFrame, setCurrentFrame] = useState(1);
  const [guess, setGuess] = useState('');
  const [won, setWon] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [status, setStatus] = useState(null);
  const advanceTimerRef = useRef(null);
  const dismissTimerRef = useRef(null);

  const clearStatusTimers = useCallback(() => {
    if (advanceTimerRef.current) {
      clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = null;
    }
    if (dismissTimerRef.current) {
      clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
  }, []);

  const showStatus = useCallback((type, message, autoDismissMs) => {
    setStatus({ type, message });
    if (autoDismissMs) {
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = setTimeout(() => {
        setStatus(null);
        dismissTimerRef.current = null;
      }, autoDismissMs);
    }
  }, []);

  useEffect(() => () => clearStatusTimers(), [clearStatusTimers]);

  useEffect(() => {
    if (status?.type !== 'loading') return undefined;
    const fallback = setTimeout(() => setStatus(null), 1500);
    return () => clearTimeout(fallback);
  }, [currentFrame, status]);

  useEffect(() => {
    let cancelled = false;
    const url = SONG_BOT_API ? `${SONG_BOT_API}/api/getsongbotq-s3` : '/api/getsongbotq-s3';
    fetch(url)
      .then((res) => {
        const contentType = res.headers.get('content-type') || '';
        if (!contentType.includes('application/json')) {
          return res.text().then((text) => {
            throw new Error(
              res.ok
                ? 'Server did not return JSON. Is the Song Bot API running?'
                : `Song Bot API error (${res.status}). Make sure the backend is running at ${url}.`
            );
          });
        }
        if (!res.ok) {
          throw new Error(`Song Bot API error: ${res.status} ${res.statusText}`);
        }
        return res.json();
      })
      .then((arr) => {
        if (cancelled) return;
        const item = Array.isArray(arr) ? arr[0] : arr;
        if (item) {
          setData(item);
        } else {
          setError('No song data');
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Could not load song.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const advanceToNextFrame = useCallback(() => {
    showStatus('loading', 'Loading next clue/frame…');
    setGuess('');
    if (currentFrame < 4) {
      setCurrentFrame((c) => c + 1);
    } else {
      clearStatusTimers();
      setShowResult(true);
    }
  }, [currentFrame, clearStatusTimers, showStatus]);

  const handleFrameLoaded = useCallback(() => {
    if (status?.type === 'loading') {
      clearStatusTimers();
      setStatus(null);
    }
  }, [status, clearStatusTimers]);

  const handleSubmitGuess = (e) => {
    e.preventDefault();
    if (!data) return;

    clearStatusTimers();

    if (!guess.trim()) {
      showStatus('empty', 'Please enter a song title before submitting.', 2000);
      return;
    }

    const correct = data.songtitle || data.answer;
    const isCorrect = isCloseMatch(guess.trim(), correct);

    if (isCorrect) {
      showStatus('correct', 'Correct!');
      setWon(true);
      advanceTimerRef.current = setTimeout(() => setShowResult(true), 1250);
    } else {
      showStatus('wrong', 'Wrong!');
      advanceTimerRef.current = setTimeout(() => {
        advanceToNextFrame();
      }, 1000);
    }
  };

  const handleNextFrame = () => {
    clearStatusTimers();
    setStatus(null);
    setGuess('');
    if (currentFrame < 4) {
      setCurrentFrame((c) => c + 1);
    } else {
      setShowResult(true);
    }
  };

  const imageUrl = (filename) => {
    if (!filename) return '';
    const base = (SONG_BOT_API || '').replace(/\/$/, '');
    return `${filename}`;
  };

  if (loading) {
    return (
      <div className="songbotz songbotz--loading">
        <div className="songbotz-spinner" aria-hidden />
        <p className="songbotz-loading-text">Loading Song Botz…</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="songbotz songbotz--error">
        <p className="songbotz-error-text">{error || 'No song data'}</p>
      </div>
    );
  }

  if (showResult) {
    const title = tidyText(data.songtitle || data.answer);
    const videoid = data.videoid || '';
    return (
      <div className="songbotz songbotz--result">
        <h1 className="songbotz-result-title">
          {won ? 'You got it!' : 'Correct song'}
        </h1>
        <p className="songbotz-result-song">{title}</p>
        {videoid && (
          <div className="songbotz-video-wrap">
            <iframe
              title="Song video"
              className="songbotz-video"
              src={`https://www.youtube.com/embed/${videoid}?autoplay=0`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        )}
      </div>
    );
  }

  const frameKey = FRAME_KEYS[currentFrame - 1];
  const frameFile = data[frameKey];

  return (
    <div className="songbotz">
      <h1 className="songbotz-heading">Song Botz</h1>
      <p className="songbotz-subheading">Guess the song from the frames</p>
      <p className="songbotz-frame-label">Frame {currentFrame} of 4</p>

      <div className="songbotz-frame-wrap">
        {frameFile ? (
          <img
            src={imageUrl(frameFile)}
            alt={`Frame ${currentFrame}`}
            className="songbotz-frame-img"
            onLoad={handleFrameLoaded}
          />
        ) : (
          <div className="songbotz-frame-placeholder">No image</div>
        )}
      </div>

      <form onSubmit={handleSubmitGuess} className="songbotz-form">
        <input
          type="text"
          value={guess}
          onChange={(e) => setGuess(e.target.value)}
          placeholder="Type the song title…"
          className="songbotz-input"
          autoComplete="off"
        />
        <div className="songbotz-actions">
          <button type="submit" className="songbotz-btn songbotz-btn--primary">
            Submit guess
          </button>
          <button
            type="button"
            className="songbotz-btn songbotz-btn--secondary"
            onClick={handleNextFrame}
          >
            {currentFrame < 4 ? 'Next frame' : 'Reveal answer'}
          </button>
        </div>
      </form>

      {status && (
        <div
          className={`songbotz-status songbotz-status--${status.type}`}
          role="status"
        >
          {status.type === 'loading' && (
            <span className="songbotz-status-spinner" aria-hidden />
          )}
          <span>{status.message}</span>
        </div>
      )}
    </div>
  );
}

export default SongBotz;
